import { faHourglass, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from '@mui/material';
import { useDeleteCADJob } from '@partful/features/job/lib/useDeleteCADJob';
import { Badge } from '@partful/shared/ui/Badge';
import { Button } from '@partful/shared/ui/Button';
import { useConfirmation } from '@partful/shared/ui/Confirmation/useConfirmation';
import { useSnackBar } from '@partful/shared/ui/SnackBar/useSnackBar';
import { Job } from '@providers/job/types';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import styles from './ProductCard.module.scss';
import SalesBOMInProgressCard from './SalesBOMInProgressCard';

interface InProgressCardProps {
  job: Job;
  redirectURL: string;
}

export default function InProgressCard({ job, redirectURL }: Readonly<InProgressCardProps>) {
  const { t } = useTranslation('productLibrary');

  /* istanbul ignore next */
  if (!job) return null;

  const { jobType, status } = job;

  if (jobType === 'CAD') {
    return (
      <AwaitingInputCard
        job={job}
        redirectURL={redirectURL}
        testID={`inProgressCAD-${status}`}
        title={t('continueCreating.title')}
        subTitle={t('continueCreating.subtitle')}
      />
    );
  }

  if (jobType === 'PartCategories' && status.includes('IN_PROGRESS_')) {
    return (
      <ProcessingProductCard
        job={job}
        redirectURL={redirectURL}
        testID={`inProgressPartCategories-${status}`}
        title={t('partsCategories.processing')}
        subTitle={t('pleaseComeBackSoon')}
      />
    );
  }

  if (jobType === 'SalesBOM') {
    return <SalesBOMInProgressCard job={job} redirectURL={redirectURL} />;
  }

  /* istanbul ignore next */
  return null; // should never reach here due to filtering...
}

interface InProgresssSubCardProps {
  job: Job;
  redirectURL: string;
  testID: string;
  title: string;
  subTitle: string;
}

function AwaitingInputCard({ job, redirectURL, testID, title, subTitle }: Readonly<InProgresssSubCardProps>) {
  const { t } = useTranslation('createProduct');
  const { t: tCommon } = useTranslation('common');
  const { mutateAsync: deleteCADJob } = useDeleteCADJob();
  const { confirm, close } = useConfirmation();
  const { showSnackBar } = useSnackBar();

  const onDeleteCADJob = () => {
    confirm({
      title: t('cad.deleteCADJob.title', { productName: job.productName }),
      message: t('cad.deleteCADJob.message'),
      confirmText: t('cad.deleteCADJob.confirmText'),
      cancelText: t('cad.deleteCADJob.cancelText'),
      onConfirm: async () => {
        await deleteCADJob({ accountId: job.account, jobId: job.runID });
        close();
        showSnackBar({
          severity: 'success',
          message: t('cad.deleteCADJob.successMessage', { productName: job.productName }),
        });
      },
    });
  };
  return (
    <Card className={styles.productCard} data-testid={testID}>
      <div className={styles.cardToolbar}>
        <Badge variant="info">{tCommon('status.inProgress')}</Badge>
        <Button variant="destructive" onClick={onDeleteCADJob} aria-label={`Delete ${job.productName}`}>
          {t('cad.deleteCADJob.deleteButton')}
        </Button>
      </div>
      <Link to={redirectURL} className={styles.cardLinkWrapper}>
        <div className={styles.cardContent}>
          <FontAwesomeIcon icon={faWandMagicSparkles} size="5x" />
          <h3>{title}</h3>
          <h4>{subTitle}</h4>
        </div>
        <div className={styles.cardTextContainer}>
          <span className={styles.configurationName}>{job.productName}</span>
        </div>
      </Link>
    </Card>
  );
}

function ProcessingProductCard({ job, redirectURL, testID, title, subTitle }: Readonly<InProgresssSubCardProps>) {
  const { t } = useTranslation('manageProduct');
  const { t: tCommon } = useTranslation('common');
  const toUrl = redirectURL;

  return (
    <Card className={styles.productCard} data-testid={testID}>
      <div className={styles.cardToolbar}>
        <Badge variant="info">{tCommon('status.updating')}</Badge>
        <Button linkComponent={NavLink} to={toUrl} variant="primary">
          {t('manageProduct')}
        </Button>
      </div>
      <Link to={toUrl} className={styles.cardLinkWrapper}>
        <div className={styles.cardContent}>
          <FontAwesomeIcon className="hourGlassIcon" icon={faHourglass} size="5x" />
          <h3>{title}</h3>
          <h4>{subTitle}</h4>
        </div>
        <div className={styles.cardTextContainer}>
          <span className={styles.configurationName}>{job.productName}</span>
        </div>
      </Link>
    </Card>
  );
}
